import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  LinearProgress,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  Typography
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { createPattern } from '../../features/green/greenSlice';
import { getGreenRinkOptions } from './greenPatternLayout';

const getRinkHeatColor = (rinkValue) => {
  const rink = Number(rinkValue);

  if (rink === 1 || rink === 6) {
    return '#ffe5e5';
  }

  if (rink === 2 || rink === 3 || rink === 4 || rink === 5) {
    return '#e8f7e8';
  }

  return 'transparent';
};

// Green Direction Dialog Component
const GreenDirectionDialog = ({
  open,
  onClose,
  greens,
  currentDirections,
  onSave,
  currentRound,
  readOnly = false
}) => {
  const [directions, setDirections] = useState(currentDirections || {});

  useEffect(() => {
    setDirections(currentDirections || {});
  }, [currentDirections, open]);

  const handleDirectionChange = (greenName, direction) => {
    setDirections((prev) => ({
      ...prev,
      [greenName]: direction
    }));
  };

  const handleSetAllToDirection = (direction) => {
    const newDirections = {};
    greens.forEach((location) => {
      location.greens.forEach((green) => {
        newDirections[`${location.id}-${green.name}`] = direction;
      });
    });
    setDirections(newDirections);
  };

  const handleSave = () => {
    onSave(directions);
    onClose();
  };

  // Get unique greens from all locations
  const uniqueGreens = [];
  greens?.forEach((location) => {
    location.greens?.forEach((green) => {
      if (!uniqueGreens.find((g) => g.name === green.name && g.locationId === location.id)) {
        uniqueGreens.push({
          name: green.name,
          locationId: location.id,
          locationName: location.name,
          fullName: `${location.name} - ${green.name}`
        });
      }
    });
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Set Green Directions - Round {currentRound + 1}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Set the direction for each green for Round {currentRound + 1}. This will apply to all
          games using that green in this round only.
        </Typography>

        {/* Set All Buttons */}
        <Box sx={{ mb: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
          <Button
            variant="outlined"
            size="small"
            disabled={readOnly}
            onClick={() => handleSetAllToDirection(1)}
            sx={{ minWidth: 140 }}>
            Set All to Direction 1
          </Button>
          <Button
            variant="outlined"
            size="small"
            disabled={readOnly}
            onClick={() => handleSetAllToDirection(2)}
            sx={{ minWidth: 140 }}>
            Set All to Direction 2
          </Button>
        </Box>

        <Grid container spacing={2}>
          {uniqueGreens.map((green, index) => (
            <Grid item xs={12} sm={6} md={4} key={`${green.locationId}-${green.name}`}>
              <FormControl fullWidth size="small">
                <InputLabel>Green {green.fullName}</InputLabel>
                <Select
                  value={directions[`${green.locationId}-${green.name}`] || 1}
                  disabled={readOnly}
                  onChange={(e) =>
                    handleDirectionChange(`${green.locationId}-${green.name}`, e.target.value)
                  }
                  label={`Green ${green.fullName}`}>
                  <MenuItem value={1}>1: Direction 1</MenuItem>
                  <MenuItem value={2}>2: Direction 2</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          ))}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly ? (
          <Button variant="contained" onClick={handleSave}>
            Save Directions
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
};

const GreenPatternBuilderDialog = ({
  open,
  onClose,
  event,
  sections = [],
  rounds = 1,
  gamesPerRound = 1,
  greens = [], // This is now an array of locations
  onSave,
  readOnly = false,
  initialPatternId = '',
  venueOptions = [],
  selectedVenueIds = [],
  onSelectedVenueIdsChange,
  fallbackGreensCount = 3,
  fallbackRinksPerGreen = 6,
  onFallbackGreensCountChange,
  onFallbackRinksPerGreenChange,
  lockVenue = false,
  hasVenueGreens = true
}) => {
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.green.isLoading);
  const patterns = useSelector((state) => state.green.patterns || []);

  const [patternName, setPatternName] = useState('');
  const [selectedPatternId, setSelectedPatternId] = useState('');
  const [numSections, setNumSections] = useState(sections.length || 6);
  const [numRounds, setNumRounds] = useState(rounds || 1);
  const [numGamesPerRound, setNumGamesPerRound] = useState(gamesPerRound || 1);
  const [selectedLocations, setSelectedLocations] = useState(greens.map((location) => location.id));
  const [isInitializing, setIsInitializing] = useState(true);
  const [greenDirectionDialogOpen, setGreenDirectionDialogOpen] = useState(false);
  const [currentDirectionRound, setCurrentDirectionRound] = useState(0);
  const isVenueManagedMode = Array.isArray(venueOptions) && venueOptions.length > 0;
  const openingSpecificPattern = Boolean(initialPatternId);
  const sortedVenueOptions = [...venueOptions].sort((a, b) =>
    String(a?.name || '').localeCompare(String(b?.name || ''))
  );
  const selectedVenueOptions = sortedVenueOptions.filter((venue) =>
    selectedVenueIds.includes(venue.id)
  );

  // Change greenDirections to be per round: { roundIdx: { locationId-greenName: direction } }
  const [greenDirectionsByRound, setGreenDirectionsByRound] = useState({});

  const locationsToUse = greens.filter((location) => selectedLocations.includes(location.id));
  const greenRinkOptions = getGreenRinkOptions(locationsToUse);

  const safeNumSections = Number.isFinite(numSections) && numSections > 0 ? numSections : 1;
  const safeNumGamesPerRound =
    Number.isFinite(numGamesPerRound) && numGamesPerRound > 0 ? numGamesPerRound : 1;
  const safeNumRounds = Number.isFinite(numRounds) && numRounds > 0 ? numRounds : 1;
  const totalGames = safeNumSections * safeNumGamesPerRound;
  const totalCells = totalGames * safeNumRounds;

  const [pattern, setPattern] = useState(
    Array.from({ length: totalGames }, () =>
      Array.from({ length: safeNumRounds }, () => ({
        green: '',
        rink: '',
        locationId: '',
        greenDirection: 1
      }))
    )
  );

  const prevStructure = useRef({ numSections, numRounds, numGamesPerRound });

  const assignedCellsCount = pattern.reduce((acc, row) => {
    return (
      acc +
      row.reduce((roundAcc, cell) => {
        return roundAcc + (cell.green && cell.rink ? 1 : 0);
      }, 0)
    );
  }, 0);

  const completionPercent = totalCells > 0 ? Math.round((assignedCellsCount / totalCells) * 100) : 0;

  // Initialize the dialog when it opens
  useEffect(() => {
    if (open) {
      setIsInitializing(true);
      setSelectedPatternId('');

      if (!initialPatternId) {
        const nextSections = sections.length || 6;
        const nextRounds = rounds || 1;
        const nextGamesPerRound = gamesPerRound || 1;

        setPatternName('');
        setNumSections(nextSections);
        setNumRounds(nextRounds);
        setNumGamesPerRound(nextGamesPerRound);
        setSelectedLocations(greens.map((location) => location.id));
        setPattern(
          Array.from({ length: nextSections * nextGamesPerRound }, () =>
            Array.from({ length: nextRounds }, () => ({
              green: '',
              rink: '',
              locationId: '',
              greenDirection: 1
            }))
          )
        );
        prevStructure.current = {
          numSections: nextSections,
          numRounds: nextRounds,
          numGamesPerRound: nextGamesPerRound
        };
      }

      // Initialize green directions with default values for each round
      const initialDirectionsByRound = {};
      const roundsForDirections = !initialPatternId ? rounds || 1 : numRounds;
      for (let roundIdx = 0; roundIdx < roundsForDirections; roundIdx++) {
        const roundDirections = {};
        greens.forEach((location) => {
          location.greens.forEach((green) => {
            roundDirections[`${location.id}-${green.name}`] = 1;
          });
        });
        initialDirectionsByRound[roundIdx] = roundDirections;
      }
      setGreenDirectionsByRound(initialDirectionsByRound);

      // Simulate initialization delay to show loader
      const initTimer = setTimeout(() => {
        setIsInitializing(false);
      }, 500);

      return () => clearTimeout(initTimer);
    }
  }, [open, greens, initialPatternId, sections.length, rounds, gamesPerRound]);

  useEffect(() => {
    if (!open || !isVenueManagedMode) {
      return;
    }

    setSelectedLocations(greens.map((location) => location.id));
  }, [open, isVenueManagedMode, greens]);

  useEffect(() => {
    if (!open || isInitializing) return;
    if (
      prevStructure.current.numSections !== numSections ||
      prevStructure.current.numRounds !== numRounds ||
      prevStructure.current.numGamesPerRound !== numGamesPerRound
    ) {
      const newPattern = Array.from({ length: numSections * numGamesPerRound }, (_, gameIdx) =>
        Array.from({ length: numRounds }, (_, roundIdx) =>
          pattern[gameIdx] && pattern[gameIdx][roundIdx]
            ? { ...pattern[gameIdx][roundIdx] }
            : { green: '', rink: '', locationId: '', greenDirection: 1 }
        )
      );
      setPattern(newPattern);

      // Update directions structure when rounds change
      setGreenDirectionsByRound((prev) => {
        const newDirectionsByRound = {};
        for (let roundIdx = 0; roundIdx < numRounds; roundIdx++) {
          if (prev[roundIdx]) {
            newDirectionsByRound[roundIdx] = prev[roundIdx];
          } else {
            const roundDirections = {};
            greens.forEach((location) => {
              location.greens.forEach((green) => {
                roundDirections[`${location.id}-${green.name}`] = 1;
              });
            });
            newDirectionsByRound[roundIdx] = roundDirections;
          }
        }
        return newDirectionsByRound;
      });

      prevStructure.current = { numSections, numRounds, numGamesPerRound };
    }
    // eslint-disable-next-line
  }, [open, numSections, numRounds, numGamesPerRound, isInitializing]);

  // Update pattern when green directions change
  useEffect(() => {
    setPattern((prev) => {
      return prev.map((gameRow) =>
        gameRow.map((cell, roundIdx) => {
          if (cell.green && cell.locationId) {
            const directionKey = `${cell.locationId}-${cell.green}`;
            const roundDirections = greenDirectionsByRound[roundIdx] || {};
            return {
              ...cell,
              greenDirection: roundDirections[directionKey] || 1
            };
          }
          return cell;
        })
      );
    });
  }, [greenDirectionsByRound]);

  // Function to generate direction summary text for a specific round
  const getDirectionSummary = (roundIdx) => {
    const summaryParts = [];
    const roundDirections = greenDirectionsByRound[roundIdx] || {};

    // Get unique greens from selected locations
    const uniqueGreens = [];
    locationsToUse.forEach((location) => {
      location.greens.forEach((green) => {
        if (!uniqueGreens.find((g) => g.name === green.name && g.locationId === location.id)) {
          uniqueGreens.push({
            name: green.name,
            locationId: location.id,
            locationName: location.name
          });
        }
      });
    });

    // Sort greens alphabetically by name for consistent display
    uniqueGreens.sort((a, b) => a.name.localeCompare(b.name));

    uniqueGreens.forEach((green) => {
      const directionKey = `${green.locationId}-${green.name}`;
      const direction = roundDirections[directionKey] || 1;
      summaryParts.push(`${green.name}:${direction}`);
    });

    return summaryParts.join(', ');
  };

  const getGameLabel = (gameIdx) => {
    const sectionIdx = Math.floor(gameIdx / numGamesPerRound);
    const gameNum = (gameIdx % numGamesPerRound) + 1;
    return `Section ${sectionIdx + 1} Game ${gameNum}`;
  };

  const handleChange = (gameIdx, roundIdx, value) => {
    setPattern((prev) => {
      const updated = prev.map((row) => row.map((cell) => ({ ...cell })));
      const directionKey =
        value.locationId && value.green ? `${value.locationId}-${value.green}` : '';
      const roundDirections = greenDirectionsByRound[roundIdx] || {};
      updated[gameIdx][roundIdx] = {
        green: value.green || '',
        rink: value.rink || '',
        locationId: value.locationId || '',
        greenDirection: directionKey ? roundDirections[directionKey] || 1 : 1
      };
      return updated;
    });
  };

  const handleOpenDirectionDialog = (roundIdx) => {
    setCurrentDirectionRound(roundIdx);
    setGreenDirectionDialogOpen(true);
  };

  const handleSave = () => {
    const flatPattern = [];
    for (let sectionIdx = 0; sectionIdx < numSections; sectionIdx++) {
      for (let gameIdx = 0; gameIdx < numGamesPerRound; gameIdx++) {
        const globalGameIdx = sectionIdx * numGamesPerRound + gameIdx;
        for (let roundIdx = 0; roundIdx < numRounds; roundIdx++) {
          flatPattern.push({
            sectionIdx,
            gameIdx,
            roundIdx,
            green: pattern[globalGameIdx][roundIdx].green,
            rink: pattern[globalGameIdx][roundIdx].rink,
            locationId: pattern[globalGameIdx][roundIdx].locationId,
            greenDirection: pattern[globalGameIdx][roundIdx].greenDirection
          });
        }
      }
    }

    // Calculate total rinks across all selected locations
    const totalRinks = locationsToUse.reduce((acc, location) => {
      return (
        acc +
        location.greens.reduce((greenAcc, green) => {
          return greenAcc + (green.rinks ? green.rinks.filter((rink) => rink.active).length : 6);
        }, 0)
      );
    }, 0);

    const patternObj = {
      name: patternName,
      sectionsCount: numSections,
      roundsCount: numRounds,
      gamesPerRound: numGamesPerRound,
      greensCount: locationsToUse.reduce((acc, location) => acc + location.greens.length, 0),
      rinksCount: totalRinks,
      locationsCount: locationsToUse.length,
      pattern: flatPattern,
      greenDirectionsByRound: greenDirectionsByRound, // Updated to save per-round directions
      venueId: locationsToUse[0]?.id || '',
      venueName: locationsToUse[0]?.name || '',
      venueLayout: Array.isArray(locationsToUse[0]?.greens) ? locationsToUse[0].greens : [],
      venueIds: locationsToUse.map((location) => location.id),
      venueNames: locationsToUse.map((location) => location.name),
      venueLayouts: locationsToUse.map((location) => ({
        id: location.id,
        name: location.name,
        greens: location.greens || []
      })),
      fallbackGreensCount: Number(fallbackGreensCount) || 0,
      fallbackRinksPerGreen: Number(fallbackRinksPerGreen) || 0
    };

    dispatch(createPattern(patternObj))
      .unwrap()
      .then((result) => {
        if (onSave) onSave(result);
        onClose();
      })
      .catch(() => {
        onClose();
      });
  };

  const buildDefaultDirectionsByRound = (roundsCount) => {
    const directionsByRound = {};
    for (let roundIdx = 0; roundIdx < roundsCount; roundIdx++) {
      const roundDirections = {};
      greens.forEach((location) => {
        (location.greens || []).forEach((green) => {
          roundDirections[`${location.id}-${green.name}`] = 1;
        });
      });
      directionsByRound[roundIdx] = roundDirections;
    }
    return directionsByRound;
  };

  const handleLoadPattern = () => {
    const selectedPattern = patterns.find((item) => item.id === selectedPatternId);
    if (!selectedPattern) {
      return;
    }

    const loadedSections = Math.max(1, Number(selectedPattern.sectionsCount) || 1);
    const loadedRounds = Math.max(1, Number(selectedPattern.roundsCount) || 1);
    const loadedGamesPerRound = Math.max(1, Number(selectedPattern.gamesPerRound) || 1);

    const nextPattern = Array.from({ length: loadedSections * loadedGamesPerRound }, () =>
      Array.from({ length: loadedRounds }, () => ({
        green: '',
        rink: '',
        locationId: '',
        greenDirection: 1
      }))
    );

    const entries = Array.isArray(selectedPattern.pattern) ? selectedPattern.pattern : [];
    entries.forEach((entry) => {
      const sectionIdx = Number(entry.sectionIdx);
      const gameIdx = Number(entry.gameIdx);
      const roundIdx = Number(entry.roundIdx);

      if (
        Number.isInteger(sectionIdx) &&
        Number.isInteger(gameIdx) &&
        Number.isInteger(roundIdx) &&
        sectionIdx >= 0 &&
        gameIdx >= 0 &&
        roundIdx >= 0 &&
        sectionIdx < loadedSections &&
        gameIdx < loadedGamesPerRound &&
        roundIdx < loadedRounds
      ) {
        const globalGameIdx = sectionIdx * loadedGamesPerRound + gameIdx;
        nextPattern[globalGameIdx][roundIdx] = {
          green: entry.green || '',
          rink: entry.rink || '',
          locationId: entry.locationId || '',
          greenDirection: Number(entry.greenDirection) || 1
        };
      }
    });

    const configuredDirections =
      selectedPattern.greenDirectionsByRound &&
      typeof selectedPattern.greenDirectionsByRound === 'object'
        ? selectedPattern.greenDirectionsByRound
        : buildDefaultDirectionsByRound(loadedRounds);

    const usedLocationIds = Array.from(
      new Set(entries.map((entry) => entry.locationId).filter((value) => Boolean(value)))
    );
    const availableLocationIds = new Set(greens.map((location) => location.id));
    const nextSelectedLocations = usedLocationIds.filter((id) => availableLocationIds.has(id));

    setPatternName(selectedPattern.name || '');
    setNumSections(loadedSections);
    setNumRounds(loadedRounds);
    setNumGamesPerRound(loadedGamesPerRound);
    if (nextSelectedLocations.length > 0) {
      setSelectedLocations(nextSelectedLocations);
    }
    setGreenDirectionsByRound(configuredDirections);
    setPattern(nextPattern);
    prevStructure.current = {
      numSections: loadedSections,
      numRounds: loadedRounds,
      numGamesPerRound: loadedGamesPerRound
    };
  };

  useEffect(() => {
    if (!open || !initialPatternId || patterns.length === 0) {
      return;
    }

    const selectedPattern = patterns.find((item) => item.id === initialPatternId);
    if (!selectedPattern) {
      return;
    }

    setSelectedPatternId(initialPatternId);
  }, [open, initialPatternId, patterns]);

  useEffect(() => {
    if (!open || !selectedPatternId || patterns.length === 0) {
      return;
    }

    handleLoadPattern();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedPatternId, patterns]);

  const handleRandomGenerate = () => {
    const newPattern = Array.from({ length: totalGames }, () =>
      Array.from({ length: numRounds }, () => ({
        green: '',
        rink: '',
        locationId: '',
        greenDirection: 1
      }))
    );

    for (let roundIdx = 0; roundIdx < numRounds; roundIdx++) {
      const combos = [...greenRinkOptions];
      // Fisher-Yates shuffle
      for (let i = combos.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [combos[i], combos[j]] = [combos[j], combos[i]];
      }
      // Assign combos to games in this round
      for (let gameIdx = 0; gameIdx < totalGames; gameIdx++) {
        const combo = combos[gameIdx % combos.length];
        const directionKey = `${combo.locationId}-${combo.green}`;
        const roundDirections = greenDirectionsByRound[roundIdx] || {};
        newPattern[gameIdx][roundIdx] = {
          green: combo.green,
          rink: combo.rink,
          locationId: combo.locationId,
          greenDirection: roundDirections[directionKey] || 1
        };
      }
    }
    setPattern(newPattern);
  };

  const handleGreenDirectionSave = (newDirections) => {
    setGreenDirectionsByRound((prev) => ({
      ...prev,
      [currentDirectionRound]: newDirections
    }));
  };

  const handleCopyRoundToAllRounds = (sourceRoundIdx = 0) => {
    setPattern((prev) => {
      return prev.map((gameRow) => {
        const source = gameRow[sourceRoundIdx] || {
          green: '',
          rink: '',
          locationId: '',
          greenDirection: 1
        };

        return gameRow.map((_, roundIdx) => {
          if (roundIdx === sourceRoundIdx) {
            return { ...source };
          }

          const directionKey =
            source.locationId && source.green ? `${source.locationId}-${source.green}` : '';
          const roundDirections = greenDirectionsByRound[roundIdx] || {};

          return {
            green: source.green || '',
            rink: source.rink || '',
            locationId: source.locationId || '',
            greenDirection: directionKey ? roundDirections[directionKey] || 1 : 1
          };
        });
      });
    });
  };

  const handleClearRound = (targetRoundIdx) => {
    setPattern((prev) => {
      return prev.map((gameRow) =>
        gameRow.map((cell, roundIdx) => {
          if (roundIdx !== targetRoundIdx) {
            return cell;
          }

          return {
            green: '',
            rink: '',
            locationId: '',
            greenDirection: 1
          };
        })
      );
    });
  };

  // Loading screen component
  const LoadingContent = () => (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="400px"
      gap={2}>
      <CircularProgress size={60} />
      <Typography variant="h6" color="text.secondary">
        Loading Pattern Builder...
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Preparing greens and rink configurations
      </Typography>
    </Box>
  );

  return (
    <>
      <Dialog open={open} onClose={onClose} fullScreen>
        <DialogTitle>
          <Box display="flex" alignItems="center" justifyContent="space-between" gap={2} flexWrap="wrap">
            <Typography variant="h6">Green/Rink Pattern Builder (V1)</Typography>
            <Box display="flex" alignItems="center" gap={1.5} flexWrap="wrap">
              <FormControl size="small" sx={{ minWidth: 280 }}>
                <InputLabel id="load-pattern-label">Load Existing Pattern</InputLabel>
                <Select
                  labelId="load-pattern-label"
                  value={selectedPatternId}
                  label="Load Existing Pattern"
                  size="small"
                  disabled={readOnly || openingSpecificPattern}
                  onChange={(e) => setSelectedPatternId(e.target.value)}>
                  <MenuItem value="">
                    <em>None</em>
                  </MenuItem>
                  {patterns.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                size="small"
                onClick={handleLoadPattern}
                disabled={readOnly || openingSpecificPattern || !selectedPatternId || isInitializing}>
                Load Pattern
              </Button>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent>
          {isInitializing ? (
            <LoadingContent />
          ) : (
            <>
              <Box
                mb={1}
                mt={1}
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
                gap={1.5}>
                <Typography variant="body2" color="text.secondary">
                  Assigned {assignedCellsCount}/{totalCells} slots ({completionPercent}%)
                </Typography>
                <Box display="flex" gap={1} flexWrap="wrap">
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={readOnly || safeNumRounds < 2}
                    onClick={() => handleCopyRoundToAllRounds(0)}>
                    Copy Round 1 To All
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={readOnly}
                    onClick={() => handleClearRound(0)}>
                    Clear Round 1
                  </Button>
                </Box>
              </Box>

              <Box mb={2}>
                <LinearProgress
                  variant="determinate"
                  value={completionPercent}
                  sx={{ height: 8, borderRadius: 999 }}
                />
              </Box>

              <Box mt={2} mb={1}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Venue Settings
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  {isVenueManagedMode ? (
                    <>
                      <Autocomplete
                        multiple
                        options={sortedVenueOptions}
                        value={selectedVenueOptions}
                        onChange={(_, selectedVenueOptionsValue) => {
                          if (typeof onSelectedVenueIdsChange === 'function') {
                            onSelectedVenueIdsChange(
                              selectedVenueOptionsValue
                                .map((venue) => String(venue?.id || '').trim())
                                .filter(Boolean)
                            );
                          }
                        }}
                        getOptionLabel={(option) => option?.name || ''}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        disabled={readOnly || lockVenue}
                        sx={{ minWidth: 260 }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Venue"
                            size="small"
                            placeholder="Type to search clubs"
                          />
                        )}
                      />

                      <TextField
                        label="Fallback Greens"
                        type="number"
                        size="small"
                        inputProps={{ min: 1 }}
                        value={fallbackGreensCount}
                        disabled={readOnly || lockVenue || hasVenueGreens}
                        onChange={(e) => {
                          if (typeof onFallbackGreensCountChange === 'function') {
                            onFallbackGreensCountChange(Math.max(1, Number(e.target.value) || 1));
                          }
                        }}
                        sx={{ width: 160 }}
                      />
                      <TextField
                        label="Fallback Rinks/Green"
                        type="number"
                        size="small"
                        inputProps={{ min: 1 }}
                        value={fallbackRinksPerGreen}
                        disabled={readOnly || lockVenue || hasVenueGreens}
                        onChange={(e) => {
                          if (typeof onFallbackRinksPerGreenChange === 'function') {
                            onFallbackRinksPerGreenChange(Math.max(1, Number(e.target.value) || 1));
                          }
                        }}
                        sx={{ width: 190 }}
                      />
                    </>
                  ) : (
                    <FormControl size="small" sx={{ minWidth: 320 }}>
                      <InputLabel id="locations-multi-label">Locations</InputLabel>
                      <Select
                        labelId="locations-multi-label"
                        multiple
                        size="small"
                        value={selectedLocations}
                        disabled={readOnly}
                        onChange={(e) => setSelectedLocations(e.target.value)}
                        input={<OutlinedInput label="Locations" />}
                        renderValue={(selected) =>
                          greens
                            .filter((location) => selected.includes(location.id))
                            .map((location) => location.name)
                            .join(', ')
                        }>
                        {greens.map((location) => (
                          <MenuItem key={location.id} value={location.id}>
                            <Checkbox checked={selectedLocations.indexOf(location.id) > -1} />
                            <ListItemText primary={location.name} />
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Box>
              </Box>

              <Box mb={2}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Pattern Details
                </Typography>
                <Box display="flex" gap={2} flexWrap="wrap">
                  <TextField
                    label="Pattern Name"
                    value={patternName}
                    size="small"
                    disabled={readOnly}
                    onChange={(e) => setPatternName(e.target.value)}
                    sx={{ minWidth: 200 }}
                  />
                  <TextField
                    label="Sections"
                    type="number"
                    size="small"
                    inputProps={{ min: 1 }}
                    value={numSections}
                    disabled={readOnly}
                    onChange={(e) => setNumSections(Math.max(1, Number(e.target.value)))}
                    sx={{ width: 120 }}
                  />
                  <TextField
                    label="Games per Section"
                    type="number"
                    size="small"
                    inputProps={{ min: 1 }}
                    value={numGamesPerRound}
                    disabled={readOnly}
                    onChange={(e) => setNumGamesPerRound(Math.max(1, Number(e.target.value)))}
                    sx={{ width: 160 }}
                  />
                  <TextField
                    label="Rounds"
                    type="number"
                    size="small"
                    inputProps={{ min: 1 }}
                    value={numRounds}
                    disabled={readOnly}
                    onChange={(e) => setNumRounds(Math.max(1, Number(e.target.value)))}
                    sx={{ width: 120 }}
                  />
                </Box>
              </Box>
              <Box sx={{ overflowX: 'auto' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <th
                        style={{
                          padding: 8,
                          borderBottom: '1px solid #ccc',
                          position: 'sticky',
                          left: 0,
                          zIndex: 3,
                          background: '#fff'
                        }}>
                        Game
                      </th>
                      {Array.from({ length: numRounds }).map((_, roundIdx) => (
                        <th key={roundIdx} style={{ padding: 8, borderBottom: '1px solid #ccc' }}>
                          <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                            <Typography variant="subtitle2">Round {roundIdx + 1}</Typography>
                            <Button
                              variant="outlined"
                              color="info"
                              size="small"
                              disabled={readOnly}
                              onClick={() => handleOpenDirectionDialog(roundIdx)}
                              sx={{
                                fontSize: '0.75rem',
                                textTransform: 'none',
                                minWidth: 'auto',
                                padding: '2px 4px'
                              }}>
                              Change Rink Direction
                            </Button>
                            <Button
                              variant="outlined"
                              color="warning"
                              size="small"
                              disabled={readOnly}
                              onClick={() => handleClearRound(roundIdx)}
                              sx={{
                                fontSize: '0.75rem',
                                textTransform: 'none',
                                minWidth: 'auto',
                                padding: '2px 4px'
                              }}>
                              Clear Round
                            </Button>
                            {/* Direction Summary for this specific round */}
                            {getDirectionSummary(roundIdx) && (
                              <Typography
                                variant="caption"
                                color="primary"
                                sx={{
                                  fontSize: '0.7rem',
                                  fontWeight: 'medium',
                                  backgroundColor: 'rgba(25, 118, 210, 0.08)',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  border: '1px solid rgba(25, 118, 210, 0.2)',
                                  maxWidth: '200px',
                                  textAlign: 'center',
                                  lineHeight: 1.2
                                }}>
                                Dir: {getDirectionSummary(roundIdx)}
                              </Typography>
                            )}
                          </Box>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {Array.from({ length: safeNumSections }).map((_, sectionIdx) => (
                      <React.Fragment key={sectionIdx}>
                        <tr>
                          <td
                            colSpan={1 + numRounds}
                            style={{
                              fontWeight: 'bold',
                              padding: 8,
                              borderBottom: '2px solid #bbb',
                              position: 'sticky',
                              left: 0,
                              zIndex: 2,
                              background: '#fff'
                            }}>
                            Section {sectionIdx + 1}
                          </td>
                        </tr>
                        {Array.from({ length: safeNumGamesPerRound }).map((_, gameIdx) => {
                          const globalGameIdx = sectionIdx * numGamesPerRound + gameIdx;
                          return (
                            <tr key={gameIdx}>
                              <td
                                style={{
                                  padding: 8,
                                  borderBottom: '1px solid #eee',
                                  position: 'sticky',
                                  left: 0,
                                  zIndex: 1,
                                  background: '#fff'
                                }}>
                                Game {gameIdx + 1}
                              </td>
                              {Array.from({ length: safeNumRounds }).map((_, roundIdx) => (
                                <td
                                  key={roundIdx}
                                  style={{ padding: 8, borderBottom: '1px solid #eee' }}>
                                  <Box display="flex" flexDirection="column" gap={1}>
                                    <Select
                                      size="small"
                                      fullWidth
                                      disabled={readOnly}
                                      value={
                                        pattern[globalGameIdx] &&
                                        pattern[globalGameIdx][roundIdx] &&
                                        pattern[globalGameIdx][roundIdx].green
                                          ? `${pattern[globalGameIdx][roundIdx].locationId}-${pattern[globalGameIdx][roundIdx].green}-${pattern[globalGameIdx][roundIdx].rink}`
                                          : ''
                                      }
                                      onChange={(e) => {
                                        const opt = greenRinkOptions.find(
                                          (o) =>
                                            `${o.locationId}-${o.green}-${o.rink}` ===
                                            e.target.value
                                        );
                                        handleChange(
                                          globalGameIdx,
                                          roundIdx,
                                          opt || { green: '', rink: '', locationId: '' }
                                        );
                                      }}
                                      displayEmpty
                                      sx={{
                                        minWidth: 120,
                                        width: '100%',
                                        backgroundColor: getRinkHeatColor(
                                          pattern[globalGameIdx]?.[roundIdx]?.rink
                                        )
                                      }}>
                                      <MenuItem value="">N/A</MenuItem>
                                      {greenRinkOptions.map((opt, index) => (
                                        <MenuItem
                                          key={index}
                                          value={`${opt.locationId}-${opt.green}-${opt.rink}`}
                                          sx={{ backgroundColor: getRinkHeatColor(opt.rink) }}>
                                          {opt.label}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    {pattern[globalGameIdx] &&
                                      pattern[globalGameIdx][roundIdx] &&
                                      pattern[globalGameIdx][roundIdx].green && (
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                          textAlign="center">
                                          Dir: {pattern[globalGameIdx][roundIdx].greenDirection}
                                        </Typography>
                                      )}
                                  </Box>
                                </td>
                              ))}
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
          {!readOnly ? (
            <>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={!patternName || isLoading || isInitializing}
                startIcon={isLoading ? <CircularProgress size={18} /> : null}>
                {isLoading ? 'Saving...' : 'Save Pattern'}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleRandomGenerate}
                disabled={greenRinkOptions.length < totalGames || isInitializing}
                sx={{ mr: 2 }}>
                Random Generate
              </Button>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => {
                  setPattern(
                    Array.from({ length: totalGames }, () =>
                      Array.from({ length: safeNumRounds }, () => ({
                        green: '',
                        rink: '',
                        locationId: '',
                        greenDirection: 1
                      }))
                    )
                  );
                }}
                disabled={isInitializing}
                sx={{ mr: 2 }}>
                Clear All
              </Button>
            </>
          ) : null}
        </DialogActions>
      </Dialog>

      {/* Green Direction Dialog */}
      <GreenDirectionDialog
        open={greenDirectionDialogOpen}
        onClose={() => setGreenDirectionDialogOpen(false)}
        greens={locationsToUse}
        currentDirections={greenDirectionsByRound[currentDirectionRound] || {}}
        onSave={handleGreenDirectionSave}
        currentRound={currentDirectionRound}
        readOnly={readOnly}
      />
    </>
  );
};

export default GreenPatternBuilderDialog;
